import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, MapPin, Star, Plane, Briefcase, Clock, Palmtree, ShieldCheck, UserCheck, Car, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { BookingForm } from './Reservation';
import { mockData } from '../mock';

const Home = () => {
  const iconMap = {
    plane: Plane,
    briefcase: Briefcase,
    clock: Clock,
    palmtree: Palmtree,
    'shield-check': ShieldCheck,
    'user-check': UserCheck,
    'clock-check': Clock,
    car: Car
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950"></div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-600 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-900 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <div className="inline-block px-4 py-2 bg-amber-600/10 border border-amber-600/30 rounded-full">
              <span className="text-amber-500 text-sm font-medium">Service Premium VTC</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              {mockData.hero.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              {mockData.hero.subtitle}
            </p>
            
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {mockData.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <a href="#reservation">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50" data-testid="hero-reserve-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  Réserver maintenant
                </Button>
              </a>
              <a
                href={`https://wa.me/${mockData.company.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                    window.gtag('event', 'whatsapp_click', {
                      event_category: 'conversion',
                      event_label: 'Contact WhatsApp'
                    });
                  }
                }}
              >
                <Button className="bg-[#25D366] hover:bg-[#1da851] text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg hover:shadow-[#25D366]/40" data-testid="hero-whatsapp-btn">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Contacter sur WhatsApp
                </Button>
              </a>
              <Link to="/services">
                <Button variant="outline" className="border-2 border-slate-700 text-white hover:bg-slate-900 text-lg px-8 py-6 transition-all duration-300">
                  Nos Services
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-8 pt-12 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-amber-500" />
                <span>Service 5 étoiles</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span>Disponible 7j/7</span>
              </div>
              <div className="flex items-center space-x-2">
                <ShieldCheck className="w-5 h-5 text-amber-500" />
                <span>Paiement sécurisé</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-amber-500 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-amber-500 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Nos Services Premium
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Un service de transport haut de gamme adapté à tous vos besoins
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-slate-900 border-slate-800 hover:border-amber-600/50 transition-all duration-300 group hover:shadow-xl hover:shadow-amber-600/10">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-14 h-14 bg-amber-600/10 rounded-lg flex items-center justify-center group-hover:bg-amber-600/20 transition-colors duration-300">
                      {IconComponent && <IconComponent className="w-7 h-7 text-amber-500" />}
                    </div>
                    <h3 className="text-xl font-semibold text-white group-hover:text-amber-500 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center space-x-2 text-sm text-gray-300">
                          <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="outline" className="border-2 border-amber-600 text-amber-500 hover:bg-amber-600 hover:text-white transition-all duration-300">
                Découvrir tous nos services
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Zone d'Intervention
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 font-light leading-relaxed">
              AzurDriver Pro vous accompagne vers toute destination.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {mockData.coverage.map((location) => (
              <div
                key={location.city}
                className={`px-6 py-3 rounded-full border transition-all duration-300 cursor-pointer ${
                  location.popular
                    ? 'bg-amber-600/10 border-amber-600 text-amber-500 hover:bg-amber-600/20'
                    : 'bg-slate-800 border-slate-700 text-gray-300 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">{location.city}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Pourquoi Nous Choisir ?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Excellence, fiabilité et confort à chaque trajet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.whyChooseUs.map((item) => {
              const IconComponent = iconMap[item.icon];
              return (
                <div key={item.title} className="text-center space-y-4">
                  <div className="w-16 h-16 bg-amber-600/10 rounded-full flex items-center justify-center mx-auto">
                    {IconComponent && <IconComponent className="w-8 h-8 text-amber-500" />}
                  </div>
                  <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="reservation" className="py-24 bg-slate-950 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-amber-600/10 border border-amber-600/30 rounded-full mb-4">
              <span className="text-amber-500 text-sm font-medium">Réservation en ligne</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Réservez Votre Course
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Indiquez votre trajet, nous confirmons rapidement votre chauffeur.
            </p>
          </div>
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 sm:p-8">
              <BookingForm />
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Prêt à Réserver ?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Réservez en ligne en quelques secondes ou appelez-nous directement
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#reservation">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 transition-all duration-300 hover:shadow-lg hover:shadow-amber-600/50" data-testid="cta-reserve-btn">
                <Phone className="w-5 h-5 mr-2" />
                Réserver ma course
              </Button>
            </a>
            <a href={`tel:${mockData.company.phone}`}>
              <Button variant="outline" className="border-2 border-slate-700 text-white hover:bg-slate-800 text-lg px-8 py-6 transition-all duration-300">
                Appeler maintenant
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
